<template>
  <div class="globe noselect">
    <canvas ref="canvas" class="globe__canvas" aria-hidden="true"></canvas>
    <!-- Trefferfläche für den Magnet-Sog: nur die Kugel, die Labels liegen darüber -->
    <div
        v-if="!reduced"
        ref="hit"
        class="globe__hit"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @lostpointercapture="onPointerUp"
        @contextmenu.prevent
    ></div>
    <nav class="globe__labels" aria-label="Webseiten">
      <a
          v-for="(service, index) in items"
          :key="service.host"
          :href="service.url"
          target="_blank"
          rel="noopener"
          class="globe__label"
          :class="{ 'is-active': active === index }"
          :style="{ '--c': service.hex }"
          @mouseenter="setActive(index)"
          @mouseleave="setActive(null)"
          @focus="setActive(index)"
          @blur="setActive(null)"
      >
        <v-icon small :color="service.color">{{ service.icon }}</v-icon>
        <span>{{ compact ? service.short : service.host }}</span>
      </a>
    </nav>
  </div>
</template>

<script>
import colors from "vuetify/lib/util/colors";
import serviceList from "./serviceList";

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const PERSPECTIVE = 4; // Kameraabstand in Kugelradien
const DEPTH_BUCKETS = 6; // Punkte gleicher Tiefe teilen sich einen fill()
const POINT_RGB = "150, 190, 255";

// Gleichmäßig verteilte Punkte auf der Einheitskugel. `band` < 1 lässt die
// Pole frei (für die Service-Punkte, deren Labels sonst kaum wandern);
// `jitter` (in Punktabständen) bricht das sichtbare Spiralmuster auf.
function fibonacciSphere(n, band = 1, jitter = 0) {
  const points = new Float32Array(n * 3);
  const spread = jitter * Math.sqrt((4 * Math.PI) / n);
  for (let i = 0; i < n; i++) {
    const y = band * (1 - (2 * (i + 0.5)) / n);
    const r = Math.sqrt(1 - y * y);
    const phi = i * GOLDEN_ANGLE;
    const px = Math.cos(phi) * r + (Math.random() - 0.5) * spread;
    const py = y + (Math.random() - 0.5) * spread;
    const pz = Math.sin(phi) * r + (Math.random() - 0.5) * spread;
    const len = Math.sqrt(px * px + py * py + pz * pz);
    points[i * 3] = px / len;
    points[i * 3 + 1] = py / len;
    points[i * 3 + 2] = pz / len;
  }
  return points;
}

// Das "Wabern": radiale Auslenkung aus überlagerten Sinuswellen.
function wobble(x, y, z, t) {
  return (
    0.035 * Math.sin(2.2 * x + 1.1 * t) * Math.cos(1.9 * y - 0.8 * t) +
    0.025 * Math.sin(3.4 * y + 2.6 * z + 1.4 * t) +
    0.015 * Math.sin(5.1 * z - 4.2 * x - 1.9 * t)
  );
}

function smoothstep(edge0, edge1, x) {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function clamp(x, min, max) {
  return Math.min(max, Math.max(min, x));
}

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export default {
  name: "ServiceGlobe",
  data: () => ({
    active: null,
    reduced: !!(window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches),
  }),
  computed: {
    items() {
      return serviceList.map(service => {
        const hex = (colors[service.color] || colors.blue).base;
        return {
          ...service,
          short: service.host.split(".")[0],
          hex,
          rgb: hexToRgb(hex),
        };
      });
    },
    compact() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  watch: {
    compact() {
      // Labeltext (Host ↔ Kurzname) und Kugelradius hängen am Breakpoint,
      // den Vuetify erst verzögert nach dem Resize aktualisiert
      this.$nextTick(() => this.resize());
    },
  },
  mounted() {
    // Animationszustand bewusst nicht in data(): 60×/s geändert, nie gerendert.
    this.ctx = this.$refs.canvas.getContext("2d");
    this.time = 0;
    this.speed = this.reduced ? 0 : 1;
    this.targetSpeed = this.speed;
    this.lastNow = null;
    this.raf = null;
    this.anchors = fibonacciSphere(this.items.length, 0.6);
    this.labelEls = Array.from(this.$el.querySelectorAll(".globe__label"));
    this.labelAngles = this.items.map(() => null);
    this.width = 0;
    this.height = 0;
    this.pressing = false;
    this.holdTime = 0;
    this.pull = 0;
    this.pullVelocity = 0;
    this.pointerX = 0;
    this.pointerY = 0;

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.$el);
    this.resize();

    // Icon-Font kommt vom CDN und ändert die Labelbreite, sobald sie da ist
    if (document.fonts) {
      document.fonts.ready.then(() => {
        if (!this.ctx) return;
        this.measureLabels();
        this.draw(0);
      });
    }

    this.requestFrame();
  },
  beforeDestroy() {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.resizeObserver.disconnect();
    this.ctx = null; // markiert die Instanz als abgebaut, siehe requestFrame()
  },
  methods: {
    setActive(index) {
      this.active = index;
      // Bewegte Ziele trifft man schlecht: beim Hovern/Fokus anhalten
      if (!this.reduced) this.targetSpeed = index === null ? 1 : 0;
      this.requestFrame();
    },
    onPointerDown(event) {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.preventDefault();
      this.$refs.hit.setPointerCapture(event.pointerId);
      this.hitRect = this.$el.getBoundingClientRect();
      this.pressing = true;
      this.holdTime = 0;
      this.onPointerMove(event);
      this.requestFrame();
    },
    onPointerMove(event) {
      if (!this.pressing) return;
      this.pointerX = event.clientX - this.hitRect.left;
      this.pointerY = event.clientY - this.hitRect.top;
    },
    onPointerUp() {
      if (!this.pressing) return;
      this.pressing = false;
      this.requestFrame();
    },
    requestFrame() {
      // kein Neustart nach beforeDestroy (z. B. durch mouseleave beim Routenwechsel)
      if (this.raf === null && this.ctx) this.raf = requestAnimationFrame(this.frame);
    },
    frame(now) {
      this.raf = null;
      // dt gedeckelt, damit nach einem Tab-Wechsel nichts springt
      const dt = this.lastNow === null ? 0 : Math.min((now - this.lastNow) / 1000, 0.05);
      this.lastNow = now;
      this.speed += (this.targetSpeed - this.speed) * (1 - Math.exp(-dt * 4));
      this.time += dt * this.speed;

      // Magnet-Sog als gedämpfte Feder: baut sich beim Halten auf und schwingt
      // nach dem Loslassen kurz unter 0 (Punkte federn nach außen zurück)
      if (this.pressing) this.holdTime += dt;
      const pullTarget = this.pressing ? 0.5 + 0.9 * (1 - Math.exp(-this.holdTime)) : 0;
      this.pullVelocity += (60 * (pullTarget - this.pull) - 5 * this.pullVelocity) * dt;
      this.pull += this.pullVelocity * dt;
      const pullSettling = Math.abs(this.pull) > 0.001 || Math.abs(this.pullVelocity) > 0.001;

      this.draw(dt);

      if (!this.reduced && (this.speed > 0.002 || this.targetSpeed > 0 || this.pressing || pullSettling)) {
        this.requestFrame();
      } else {
        this.lastNow = null;
      }
    },
    resize() {
      const width = this.$el.clientWidth;
      const height = this.$el.clientHeight;
      if (!this.ctx || !width || !height) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = this.$refs.canvas;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      this.width = width;
      this.height = height;
      // Handy: Labels dürfen die Kugel seitlich überlappen, dafür größer
      this.radius = Math.min(width * (this.compact ? 0.36 : 0.3), height * 0.34);
      this.pointSize = 0.7 + this.radius / 600;

      const count = Math.round(clamp(this.radius * this.radius * 0.03, 700, 2600));
      if (!this.points || this.points.length !== count * 3) {
        this.points = fibonacciSphere(count, 1, 0.6);
        this.projX = new Float32Array(count);
        this.projY = new Float32Array(count);
        this.projR = new Float32Array(count);
        this.projBucket = new Uint8Array(count);
      }

      if (this.$refs.hit) {
        const size = `${Math.round(this.radius * 2.12)}px`;
        this.$refs.hit.style.width = size;
        this.$refs.hit.style.height = size;
      }

      this.measureLabels();
      this.draw(0); // Canvas ist nach dem Resize leer — sofort neu zeichnen
    },
    measureLabels() {
      this.labelSizes = this.labelEls.map(el => ({ w: el.offsetWidth, h: el.offsetHeight }));
    },
    draw(dt) {
      const { ctx, width, height, radius, time: t } = this;
      if (!ctx || !width) return;
      const cx = width / 2;
      const cy = height / 2;

      // Drehung um die Hochachse, dazu leichtes Nicken und Schwanken
      const yaw = t * 0.1;
      const tilt = 0.32 + 0.1 * Math.sin(t * 0.23);
      const sway = 0.06 * Math.sin(t * 0.17);
      const cyw = Math.cos(yaw), syw = Math.sin(yaw);
      const ctl = Math.cos(tilt), stl = Math.sin(tilt);
      const csw = Math.cos(sway), ssw = Math.sin(sway);
      const breathe = 1 + 0.015 * Math.sin(t * 0.7);

      // Projiziert einen Einheitsvektor, um `scale` radial ausgelenkt.
      // Liefert Bildschirmposition, Perspektivfaktor und Tiefe der Normalen.
      const out = { x: 0, y: 0, f: 1, nz: 0 };
      const project = (x, y, z, scale) => {
        const x1 = x * cyw + z * syw;
        const z1 = -x * syw + z * cyw;
        const y2 = y * ctl - z1 * stl;
        const z2 = y * stl + z1 * ctl;
        const x3 = x1 * csw - y2 * ssw;
        const y3 = x1 * ssw + y2 * csw;
        const f = PERSPECTIVE / (PERSPECTIVE - z2 * scale);
        out.x = cx + x3 * scale * f * radius;
        out.y = cy - y3 * scale * f * radius;
        out.f = f;
        out.nz = z2;
        return out;
      };

      // Magnet-Sog: Anteil, um den ein Bildschirmpunkt zum Zeiger rückt
      // (negativ beim Nachfedern). Hintere Punkte (depth → 0) folgen schwächer.
      const { pull, pointerX: mx, pointerY: my } = this;
      const magnetOn = Math.abs(pull) > 0.001;
      const falloff = 1 / (2 * (radius * 0.55) ** 2);
      const attract = (x, y, depth) => (magnetOn
        ? clamp(pull * Math.exp(-((mx - x) ** 2 + (my - y) ** 2) * falloff) * (0.6 + 0.4 * depth), -0.85, 0.96)
        : 0);

      ctx.globalCompositeOperation = "source-over";
      ctx.clearRect(0, 0, width, height);

      const glow = ctx.createRadialGradient(cx, cy, radius * 0.2, cx, cy, radius * 1.7);
      glow.addColorStop(0, "rgba(70, 110, 255, 0.10)");
      glow.addColorStop(1, "rgba(70, 110, 255, 0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, width, height);

      // Punktwolke: additiv geblendet, daher ohne Tiefensortierung
      const { points, projX, projY, projR, projBucket, pointSize } = this;
      const count = projX.length;
      for (let i = 0; i < count; i++) {
        const x = points[i * 3], y = points[i * 3 + 1], z = points[i * 3 + 2];
        const p = project(x, y, z, (1 + wobble(x, y, z, t)) * breathe);
        const depth = (p.nz + 1) / 2;
        const move = attract(p.x, p.y, depth);
        projX[i] = p.x + (mx - p.x) * move;
        projY[i] = p.y + (my - p.y) * move;
        projR[i] = (0.55 + 0.75 * depth) * p.f * pointSize * (1 + 1.5 * Math.abs(move));
        projBucket[i] = Math.min(DEPTH_BUCKETS - 1, Math.floor(depth * DEPTH_BUCKETS));
      }
      ctx.globalCompositeOperation = "lighter";
      for (let b = 0; b < DEPTH_BUCKETS; b++) {
        ctx.beginPath();
        for (let i = 0; i < count; i++) {
          if (projBucket[i] !== b) continue;
          ctx.moveTo(projX[i] + projR[i], projY[i]);
          ctx.arc(projX[i], projY[i], projR[i], 0, Math.PI * 2);
        }
        const alpha = 0.1 + 0.8 * Math.pow((b + 0.5) / DEPTH_BUCKETS, 1.6);
        ctx.fillStyle = `rgba(${POINT_RGB}, ${alpha.toFixed(3)})`;
        ctx.fill();
      }

      if (pull > 0.001) {
        const glowRadius = radius * 0.6;
        const core = ctx.createRadialGradient(mx, my, 0, mx, my, glowRadius);
        core.addColorStop(0, `rgba(${POINT_RGB}, ${(0.38 * Math.min(pull, 1.4)).toFixed(3)})`);
        core.addColorStop(1, `rgba(${POINT_RGB}, 0)`);
        ctx.fillStyle = core;
        ctx.fillRect(mx - glowRadius, my - glowRadius, glowRadius * 2, glowRadius * 2);
      }

      this.drawServices(project, attract, cx, cy, breathe, dt);
    },
    // Service-Punkte samt Leader-Linien; die HTML-Labels werden direkt per
    // Style verschoben. Jedes Label sitzt auf einer Ellipse um die Kugel, in
    // Richtung seines Punkts — geglättet, damit es beim Überqueren der Mitte
    // nicht springt, sondern um die Kugel herumschwingt.
    drawServices(project, attract, cx, cy, breathe, dt) {
      const { ctx, width, height, radius, anchors, items, labelEls, labelSizes, labelAngles, time: t } = this;
      const ringX = radius * 1.3;
      const ringY = radius * 1.2;
      const stub = this.compact ? 6 : 12;
      const gap = 4;
      const margin = 8;

      for (let i = 0; i < items.length; i++) {
        const x = anchors[i * 3], y = anchors[i * 3 + 1], z = anchors[i * 3 + 2];
        const p = project(x, y, z, (1 + wobble(x, y, z, t)) * breathe);
        const f = p.f;
        const facing = smoothstep(-0.2, 0.2, p.nz);
        const isActive = this.active === i;
        const rgb = items[i].rgb;

        // Punkt folgt dem Sog, der Labelwinkel bleibt an der unverzerrten
        // Lage — sonst würde der Sog die Labels um die Kugel reißen
        const target = Math.atan2(p.y - cy, p.x - cx);
        const move = attract(p.x, p.y, (p.nz + 1) / 2);
        const dotX = p.x + (this.pointerX - p.x) * move;
        const dotY = p.y + (this.pointerY - p.y) * move;
        if (labelAngles[i] === null) {
          labelAngles[i] = target;
        } else {
          let diff = target - labelAngles[i];
          diff = Math.atan2(Math.sin(diff), Math.cos(diff)); // auf [-π, π]
          labelAngles[i] += diff * (1 - Math.exp(-dt * 7));
        }
        const angle = labelAngles[i];
        const cos = Math.cos(angle), sin = Math.sin(angle);

        // seitwärts: waagerechter Stummel + Label daneben;
        // oben/unten: Label mittig darüber bzw. darunter
        const side = clamp(cos * 2.2, -1, 1);
        const elbowX = cx + cos * ringX;
        const elbowY = cy + sin * ringY;
        const stubX = elbowX + side * stub;
        const { w, h } = labelSizes[i];
        const labelX = clamp(stubX + side * (w / 2 + gap), w / 2 + margin, width - w / 2 - margin);
        const labelY = clamp(
          elbowY + (1 - Math.abs(side)) * Math.sign(sin) * (h / 2 + gap),
          h / 2 + margin,
          height - h / 2 - margin
        );

        // Labels sind immer sichtbar; nur Linie und Punkt zeigen die Tiefe
        const lineAlpha = isActive ? 0.95 : 0.25 + 0.4 * facing;
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = `rgba(${rgb}, ${lineAlpha.toFixed(3)})`;
        ctx.lineWidth = isActive ? 1.5 : 1;
        ctx.beginPath();
        ctx.moveTo(dotX, dotY);
        ctx.lineTo(elbowX, elbowY);
        ctx.lineTo(stubX, elbowY);
        ctx.stroke();

        const dotAlpha = isActive ? 1 : 0.25 + 0.75 * facing;
        const haloRadius = (isActive ? 24 : 14) * f;
        ctx.globalCompositeOperation = "lighter";
        const halo = ctx.createRadialGradient(dotX, dotY, 0, dotX, dotY, haloRadius);
        halo.addColorStop(0, `rgba(${rgb}, ${(0.6 * dotAlpha).toFixed(3)})`);
        halo.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.fillStyle = halo;
        ctx.fillRect(dotX - haloRadius, dotY - haloRadius, haloRadius * 2, haloRadius * 2);
        ctx.fillStyle = `rgba(${rgb}, ${dotAlpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(dotX, dotY, (isActive ? 4.5 : 3.2) * f, 0, Math.PI * 2);
        ctx.fill();

        const el = labelEls[i];
        el.style.transform = `translate(${Math.round(labelX - w / 2)}px, ${Math.round(labelY - h / 2)}px)`;
        el.style.opacity = "1";
        // bei Überlappung liegt das Label des vorderen Punkts oben
        el.style.zIndex = isActive ? "20" : String(Math.round(facing * 10));
      }
    },
  },
}
</script>

<style scoped>
.globe {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
.globe__canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.globe__hit {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  cursor: crosshair;
  touch-action: none; /* Halten auf dem Handy: kein Scrollen/Zoomen */
}
.globe__label {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px 4px 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(8, 12, 22, 0.72);
  backdrop-filter: blur(4px);
  color: #c9d6ea;
  font-size: 13px;
  line-height: 1.2;
  white-space: nowrap;
  text-decoration: none;
  opacity: 0; /* bis draw() sie zum ersten Mal positioniert */
  will-change: transform;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
}
.globe__label:hover,
.globe__label:focus-visible,
.globe__label.is-active {
  border-color: var(--c);
  box-shadow: 0 0 14px var(--c);
  color: #fff;
  outline: none;
}
@media (max-width: 599px) {
  .globe__label {
    gap: 4px;
    padding: 3px 9px 3px 6px;
    font-size: 11px;
  }
}
</style>
